import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  InputAdornment,
  IconButton,
  Link
} from '@mui/material';
import {
  LockOutlined,
  EmailOutlined,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { apiService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import MarqueOptimStock from '../components/commun/MarqueOptimStock';

// D-UI-4 : sans client ID, le bouton Google est simplement MASQUÉ (config serveur absente,
// pas une erreur utilisateur). Lu au chargement du module : CRA fige les REACT_APP_* au build.
const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

/**
 * ── LE PREMIER ÉCRAN QU'UN PROSPECT VOIT ──────────────────────────────────────
 *
 * 🔴 IL PORTAIT LA MARQUE TROIS FOIS. Un dégradé violet sur TOUTE la hauteur de
 * la fenêtre (`DEGRADE_CONNEXION`), un carré de 80 px en dégradé, et le nom
 * « OptimStock » avec le dégradé coulé DANS les lettres. Trois procédés
 * décoratifs empilés sur l'écran qui doit inspirer le sérieux — *exactement le
 * « trop démo » qu'on retire partout ailleurs depuis RU2.*
 *
 * ⚠️ ET LE FOND PLEIN ÉCRAN N'AVAIT AUCUNE VARIANTE SOMBRE. `DEGRADE_CONNEXION`
 * était écrit en dur ; en mode sombre le violet restait identique pendant que la
 * carte `background.paper` s'assombrissait. Personne n'avait mesuré ce contraste
 * — et le fond neutre rend la question SANS OBJET plutôt que d'y répondre.
 * *Le meilleur correctif d'une couleur non mesurée est celui qui la supprime.*
 *
 * ⚠️ LE BOUTON DE CONNEXION N'EST PLUS UN CAS PARTICULIER. Il portait ses
 * propres dégradés — normal, survol, désactivé — recopiés dans son `sx`. Or le
 * thème donne DÉJÀ `background: degrade()` et `DEGRADE_SURVOL` à tout
 * `variant="contained"` (`creerTheme.js:172-175`). Le bouton d'ici est donc
 * redevenu le bouton de la plateforme, et `DEGRADE_CONNEXION` comme
 * `DEGRADE_DESACTIVE` sont sortis des jetons faute d'emploi.
 */
const Login = ({ onLogin }) => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  /**
   * L'erreur est stockée SOUS SA FORME, jamais déjà traduite.
   *
   * 🔴 `setError(t('auth.googleError'))` FIGEAIT LA LANGUE AU MOMENT DE
   * L'ÉCHEC. Le dictionnaire est chargé par `fetch` dans `LanguageContext` :
   * changer de langue le remplace, mais une chaîne déjà écrite dans un `useState`
   * ne repasse jamais par `t`. Le message restait dans l'ancienne langue.
   *
   * 🔴 ET C'EST L'ÉCRAN OÙ LE DÉFAUT EST QUASI CERTAIN, pas seulement possible :
   * le Login est le PREMIER rendu de l'application. Le dictionnaire n'est pas
   * encore arrivé, `t()` rend alors la CLÉ BRUTE — un utilisateur qui se trompe
   * de mot de passe dans la première seconde lisait « auth.loginError ».
   *
   * ⚠️ DEUX NATURES, DONC DEUX CHAMPS. `cle` est traduite au rendu ; `texte` est
   * un message venu du SERVEUR, déjà en clair et qu'on ne traduit pas. Les
   * confondre en une seule chaîne, c'est ce qui a créé le défaut.
   */
  const [error, setError] = useState(null);   // null | { cle } | { texte }
  const [showPassword, setShowPassword] = useState(false);

  const messageErreur = !error ? ''
    : (error.cle ? t(error.cle) : error.texte);

  // Message porté par l'ÉTAT DE NAVIGATION (react-router), pas par l'URL : un
  // paramètre traînerait dans l'historique et serait partageable. Aujourd'hui
  // un seul émetteur — la page d'acceptation d'invitation, qui n'ouvre aucune
  // session et renvoie donc ici.
  const location = useLocation();
  const navigate = useNavigate();
  const navigationMessage = location.state?.message || '';

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Ouverture de session, commune aux deux chemins de connexion (mot de passe et Google).
  // Le serveur pose les cookies httpOnly : on ne persiste QUE l'état d'UI. Factorisable
  // sans risque car _emit_session() garantit un body identique sur /auth/login et
  // /auth/google/login.
  const establishSession = (data) => {
    const { user, stocks } = data;

    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('stocks', JSON.stringify(stocks));

    // Si l'utilisateur a des stocks, sélectionner le premier par défaut
    if (stocks && stocks.length > 0) {
      const firstStock = stocks[0];
      localStorage.setItem('currentStock', JSON.stringify(firstStock));
    }

    // Appeler la fonction de connexion du parent
    onLogin(user, stocks);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.post('/auth/login', formData);
      establishSession(response.data);
    } catch (error) {
      console.error('Erreur de connexion:', error);
      const duServeur = error.response?.data?.error;
      setError(duServeur ? { texte: duServeur } : { cle: 'auth.loginError' });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async ({ credential }) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.post('/auth/google/login', { credential });
      establishSession(response.data);
    } catch (error) {
      console.error('Erreur de connexion Google:', error);
      // Credential invalide, compte non lié et compte désactivé sortent TOUS en 401 :
      // le front ne peut pas les distinguer sans matcher la chaîne d'erreur (fragile).
      // On relaie donc le message du serveur, avec un libellé traduit en secours.
      const duServeur = error.response?.data?.error;
      setError(duServeur ? { texte: duServeur } : { cle: 'auth.googleError' });
    } finally {
      setLoading(false);
    }
  };

  // Échec côté Google (popup fermée, script indisponible…) : pas de réponse HTTP à relayer.
  const handleGoogleError = () => {
    setError({ cle: 'auth.googleError' });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Box sx={{ maxWidth: 480, width: '100%' }}>
        {/* Le choix de langue est HORS de la carte, et c'est délibéré : il ne
            fait pas partie du formulaire de connexion, il gouverne la page
            entière — y compris le libellé des champs qu'on est en train de
            lire. Même position que sur le tunnel d'invitation, l'autre porte. */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <LanguageSwitcher />
        </Box>

        <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
          {/* Pastille et nom sur UNE ligne — le bloc d'identité de la
              plateforme, le même que la Sidebar et le tunnel. */}
          <MarqueOptimStock />
          <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            {t('auth.subtitle')}
          </Typography>

          {navigationMessage && !error && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              {navigationMessage}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {messageErreur}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('auth.email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
              autoComplete="email"
              autoFocus
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label={t('auth.password')}
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              required
              autoComplete="current-password"
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                      aria-label={t('auth.password')}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* 🔴 LE LIEN ARRIVE AVEC LE CHEMIN QUI LE SERT — et la garde
                `AE5` l'exigeait. Elle vérifie un APPARIEMENT, pas une
                absence : elle rougissait tant que le lien existait sans route,
                et elle rougirait désormais si la route existait sans lien.
                *On n'annonce que ce qui est servi ; et ce qui est servi doit
                s'annoncer, sinon le recours existe et reste introuvable.* */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Link component="button" type="button" variant="body2"
                    onClick={() => navigate('/mot-de-passe-oublie')}>
                {t('auth.forgotPassword')}
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('auth.loginButton')
              )}
            </Button>
          </form>

          {/* Hors du <form> : un bouton à l'intérieur en déclencherait la soumission native.
              Masqué si REACT_APP_GOOGLE_CLIENT_ID est absent (D-UI-4).
              key={language} : le script GSI n'est chargé qu'au montage du Provider
              (useEffect deps [nonce], locale non incluse) — remonter le Provider est le
              seul moyen de recharger le script avec la bonne langue de bouton. */}
          {googleClientId && (
            <>
              <Divider sx={{ my: 2 }}>{t('auth.orSeparator')}</Divider>
              <GoogleOAuthProvider key={language} clientId={googleClientId} locale={language}>
                <Box display="flex" justifyContent="center">
                  {/* ⚠️ `theme="outline"` EST ÉCRIT, pas laissé au défaut. C'est
                      la valeur par défaut de GSI aujourd'hui — mais la maquette
                      demande un CONTOUR face au bouton plein au-dessus, et une
                      hiérarchie validée ne se confie pas au défaut d'une
                      bibliothèque tierce qui peut en changer. */}
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    text="continue_with"
                    theme="outline"
                    shape="rectangular"
                    width="320"
                  />
                </Box>
              </GoogleOAuthProvider>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default Login;
